import React, { useState, useEffect } from "react";
import { IconX } from "@tabler/icons-react";
import { useForm } from "react-hook-form";
import { useOrderCreateMutation } from "../../../apis/orderApis";
import { useMerchantAllListData } from "../../../apis/merchantApis";
import { useFetchDarkStoreBranchesData } from "../../../apis/darkStoreApi";
import { populateSenderData } from "../../../utilities/location";
import toast from "react-hot-toast";
import { OrderFormData } from "../../../types/types";
import { extractErrorMessage } from "../../../utilities/apiErrorHandler";
import SenderSection from "./SenderSection";
import ReceiverSection from "./ReceiverSection";
import ShipmentSection from "./ShipmentSection";
import PreviewModal from "./PreviewModal";
import { useClientPermissions } from "../../../apis/profileApis";
import { useSelector } from "react-redux";
import { selectClientPermissions } from "../../../features/auth/authSlice";
import ShipmentItemSection from "./ShipmentItemSection";
import ToggleButton from "../../../components/ToggleButton";
import { createPortal } from "react-dom";

interface AddOrdersModalsProps {
  onClose: () => void;
  fetchData: () => void;
  isSidebar?: boolean;
}

const AddOrdersModal: React.FC<AddOrdersModalsProps> = ({
  onClose,
  fetchData,
  isSidebar = false,
}) => {
  const [step, setStep] = useState(1);
  const [showPreview, setShowPreview] = useState(false);
  const [useDifferentSender, setUseDifferentSender] = useState(false);
  const [useFMOrder, setUseFMOrder] = useState(false);
  const [isShipmentSubmitted, setIsShipmentSubmitted] = useState(false);

  const { mutate: createOrder, isPending: isRequestPending } =
    useOrderCreateMutation();

  const { data: clients } = useMerchantAllListData();

  useClientPermissions();

  const permissions = useSelector(selectClientPermissions);

  const {
    register,
    setValue,
    clearErrors,
    setError,
    formState: { errors },
    watch,
    control,
    handleSubmit,
    trigger,
  } = useForm<OrderFormData>({
    mode: "onChange",
  });

  const selectedDarkstoreId = watch("darkstoreId");

  const { data: darkStoreBranches = [] } =
    useFetchDarkStoreBranchesData(selectedDarkstoreId);

  // Clear products when FM is disabled (since only FM orders have products)
  useEffect(() => {
    if (!useFMOrder) {
      setValue("products", []);
    }
  }, [useFMOrder, setValue]);

  const selectedClientId = watch("clientId");
  useEffect(() => {
    const client = clients?.find((c: any) => c.id === selectedClientId);
    if (client?.isNippco === true) {
      setUseDifferentSender(true);
    }
  }, [selectedClientId, clients]);

  // =========================
  // STEP VALIDATION
  // =========================

  const validateStep = async (): Promise<boolean> => {
    // STEP 1 -> Sender
    if (step === 1) {
      if (useDifferentSender) {
        return await trigger([
          "sender.name",
          "sender.mobile",
          "sender.email",
          "sender.country",
          "sender.city",
          "sender.district",
          "sender.zipCode",
          "sender.address",
          "sender.nationalAddress",
        ] as any);
      } else {
        return await trigger(["clientId"]);
      }
    }

    // STEP 2 -> Receiver
    if (step === 2) {
      return await trigger([
        "receiver.name",
        "receiver.mobile",
        "receiver.email",
        "receiver.country",
        "receiver.city",
        "receiver.district",
        "receiver.address",
        "receiver.zipCode",
        "receiver.latitude",
        "receiver.longitude",
      ] as any);
    }

    // STEP 3 -> Shipment + Products
    if (step === 3) {
      const shipmentValid = await trigger([
        "shipment.referenceNumber",
        "shipment.shipmentCost",
        "shipment.paymentType",
        "shipment.numberOfPieces",
        "shipment.totalWeight",
        "shipment.serviceType",
        "shipment.shipmentType",
        "shipment.description",
      ] as any);

      if (useFMOrder) {
        const products = watch("products") || [];

        const productFields = products.flatMap((_: any, i: number) => [
          `products.${i}.pId`,
          `products.${i}.quantity`,
        ]);

        const productValid = await trigger(productFields as any);

        return shipmentValid && productValid;
      }

      return shipmentValid;
    }

    return true;
  };

  // =========================
  // NEXT STEP
  // =========================

  const handleNext = async () => {
    if (step === 3) {
      setIsShipmentSubmitted(true);
    }
    const isValid = await validateStep();

    if (!isValid) {
      toast.error("Please fill all required fields");
      return;
    }

    if (step < 3) {
      setStep((prev) => prev + 1);
    } else {
      setShowPreview(true);
    }
  };

  // =========================
  // BACK STEP
  // =========================

  const handleBack = () => {
    if (step > 1) {
      setStep((prev) => prev - 1);
    }
  };

  // =========================
  // CREATE ORDER
  // =========================

  const handleCreate = async (data: OrderFormData) => {
    const resolvedNationalAddress =
      watch("receiver.shortAddress") || watch("receiver.nationalAddress") || "";
    const resolvedSenderNationalAddress =
      watch("sender.shortAddress") || watch("sender.nationalAddress") || "";

    const updatedData = {
      ...data,
      type: "LM",
      // Removed darkstore fields from payload as requested
      // isDarkstoreOrder: useDarkstoreOrder,
      // ...(useDarkstoreOrder && {
      //   darkstoreId: data.darkstoreId,
      //   branchDarkstoreId: data.branchDarkstoreId,
      // }),

      sender: useDifferentSender
        ? {
            ...data.sender,
            nationalAddress: resolvedSenderNationalAddress,
            shortAddress: resolvedSenderNationalAddress,
          }
        : data.sender,

      receiver: {
        ...data.receiver,
        address:
          watch("receiver.address") || watch("receiver.streetName") || "",

        nationalAddress: resolvedNationalAddress,
        shortAddress: resolvedNationalAddress,
      },
    };

    createOrder(updatedData, {
      onSuccess: () => {
        setShowPreview(false);
        fetchData();
        onClose();
        toast.success("Order created successfully");
      },

      onError: (error: any) => {
        toast.error(extractErrorMessage(error, "Failed to create order"), {
          duration: 5000,
        });
      },
    });
  };

  // =========================
  // CLIENT SELECT
  // =========================

  const handleClientSelect = async (clientId: string) => {
    const sender = clients?.find((client: any) => client?.id === clientId);

    if (!sender) return;

    setValue("clientId", clientId);
    setValue("clientName", sender?.name);

    setValue("sender.name", sender?.name);
    setValue("sender.mobile", sender?.mobileNo);
    setValue("sender.email", sender?.email);

    setValue("sender.country", sender?.country);
    setValue("sender.city", sender?.city);
    setValue("sender.district", sender?.district);

    setValue("sender.countryName", sender?.countryName);
    setValue("sender.cityName", sender?.cityName);
    setValue("sender.districtName", sender?.districtName);

    // WAREhouse address should be filled correctly: sender?.location contains the warehouse address
    const warehouseAddress = sender?.location || sender?.address || "";
    setValue("sender.address", warehouseAddress);

    const isSaudiArabia =
      sender?.country === "Saudi Arabia" ||
      sender?.countryName === "Saudi Arabia" ||
      sender?.country === "1";

    if (isSaudiArabia) {
      setValue("sender.shortAddress", sender?.shortAddress || "");
      setValue("sender.nationalAddress", sender?.shortAddress || "");
    } else {
      setValue("sender.shortAddress", "");
      setValue("sender.nationalAddress", "");
    }

    setValue("sender.dialCode", sender?.dialCode);

    // Set coordinates and zip code
    if (sender?.latitude && sender?.longitude) {
      setValue("sender.latitude", sender.latitude.toString());
      setValue("sender.longitude", sender.longitude.toString());
      setValue("sender.zipCode", sender.zipCode || "");
    } else if (warehouseAddress) {
      try {
        const geocoded = await populateSenderData(sender);
        setValue("sender.latitude", geocoded?.latitude || "");
        setValue("sender.longitude", geocoded?.longitude || "");
        setValue("sender.zipCode", geocoded?.zipCode || "");
      } catch (error) {
        console.error("Geocoding failed on client select:", error);
        setValue("sender.latitude", "");
        setValue("sender.longitude", "");
        setValue("sender.zipCode", "");
      }
    } else {
      setValue("sender.latitude", "");
      setValue("sender.longitude", "");
      setValue("sender.zipCode", "");
    }

    if (sender?.access === "FM") {
      setUseFMOrder(true);
    } else if (sender?.access === "LM") {
      setUseFMOrder(false);
    } else if (sender?.access === "BOTH") {
      setUseFMOrder(true);
    }
  };

  const content = (
    <div className={`flex flex-col h-full bg-white ${isSidebar ? "rounded-3xl border border-gray-200 shadow-sm overflow-hidden" : ""}`}>
      {/* HEADER — matches Staff sidebar style */}
      <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-200 bg-white shrink-0">
        <h2 className="text-lg font-bold text-[#212121]">Create Order</h2>

        <div className="flex items-center gap-2">
          {/* FM toggle chip — only shown when client has BOTH access */}
          {clients?.find((c: any) => c.id === watch("clientId"))?.access === "BOTH" && (
            <button
              type="button"
              onClick={() => setUseFMOrder(!useFMOrder)}
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-bold transition-all ${
                useFMOrder
                  ? "bg-[#FBD037] border-[#FBD037] text-[#212121]"
                  : "bg-white border-gray-300 text-gray-500 hover:border-[#FBD037]"
              }`}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${useFMOrder ? "bg-[#212121]" : "bg-gray-400"}`} />
              FM
            </button>
          )}

          <button type="button"
            className="px-3.5 py-1.5 border border-[#D2D2D2] bg-white rounded-lg text-xs font-semibold hover:bg-slate-50 transition text-slate-700"
            onClick={showPreview ? () => setShowPreview(false) : (step === 1 ? onClose : handleBack)}>
            {showPreview ? "Back" : (step === 1 ? "Cancel" : "Back")}
          </button>

          <button type="button" onClick={showPreview ? handleSubmit(handleCreate) : handleNext} disabled={isRequestPending}
            className={`px-4 py-1.5 rounded-lg text-xs font-bold transition border ${
              isRequestPending
                ? "bg-gray-300 border-gray-300 text-gray-500 cursor-not-allowed"
                : "bg-[#FBD037] border-[#FBD037] hover:bg-yellow-400 text-[#000000]"
            }`}>
            {showPreview ? "Submit" : (step === 3 ? "Preview" : "Next")}
          </button>

          <button type="button" onClick={onClose}
            className="p-1.5 rounded-lg border border-[#D2D2D2] hover:bg-gray-100 transition-colors text-slate-700"
            title="Close">
            <IconX size={18} />
          </button>
        </div>
      </div>

      {/* BODY — Staff-form style scroll area */}
      <div className="w-full flex-1 overflow-y-auto min-h-0">
        <div className={`h-full overflow-y-auto space-y-4 p-4 scrollbar-clean`}>
          {/* STEPPER */}
          {!showPreview && (
            <div className="flex items-center justify-center gap-4 py-4 px-2">
              {[1, 2, 3].map((item) => (
                <div
                  key={item}
                  className={`w-1/3 border-b-4 rounded-t-md ${
                    step >= item
                      ? "border-[#FBD037] text-black"
                      : "border-gray-200 text-gray-500"
                  }`}
                />
              ))}
            </div>
          )}

          {/* STEP 1 */}
          {step === 1 && !showPreview && (
            <SenderSection clients={clients} register={register} errors={errors} setValue={setValue}
              clearErrors={clearErrors} watch={watch} control={control}
              useDifferentSender={useDifferentSender} setUseDifferentSender={setUseDifferentSender}
              useFMOrder={useFMOrder} setUseFMOrder={setUseFMOrder}
              onClientSelect={handleClientSelect} isSidebar={isSidebar} setError={setError} />
          )}

          {/* STEP 2 */}
          {step === 2 && !showPreview && (
            <ReceiverSection register={register} errors={errors} setValue={setValue}
              watch={watch} control={control} clearErrors={clearErrors} setError={setError} isSidebar={isSidebar} />
          )}

          {/* STEP 3 */}
          {step === 3 && !showPreview && (
            <div className="space-y-3">
              <ShipmentSection register={register} errors={errors} setValue={setValue} watch={watch}
                clearErrors={clearErrors} mode="add" open={true} onClose={onClose} setMode={() => {}} isSidebar={isSidebar}
                isSubmitted={isShipmentSubmitted} />
              {useFMOrder && permissions?.some((p: any) => p?.module === "products") && (
                <div className="relative group rounded-2xl bg-gradient-to-b from-white/90 to-white/60 border border-[#D2D2D2]/70 p-3.5 transition-all duration-300">
                  <div className="absolute inset-0 rounded-2xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />
                  <p className="relative z-10 text-[#212121]/80 pb-2 font-bold text-xs uppercase tracking-widest">Add Items</p>
                  <div className="relative z-10">
                    <ShipmentItemSection setValue={setValue} watch={watch} register={register} errors={errors} control={control} isSidebar={isSidebar} />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* PREVIEW (inline when in sidebar mode) */}
          {showPreview && isSidebar && (
            <PreviewModal
              onClose={onClose}
              onBack={() => setShowPreview(false)}
              watch={watch}
              onSubmit={handleSubmit(handleCreate)}
              isSubmitting={isRequestPending}
              isSidebar={isSidebar}
            />
          )}
        </div>
      </div>

      {/* PREVIEW MODAL (dialog mode) */}
      {showPreview && !isSidebar && (
        <PreviewModal
          onClose={onClose}
          onBack={() => setShowPreview(false)}
          watch={watch}
          onSubmit={handleSubmit(handleCreate)}
          isSubmitting={isRequestPending}
          isSidebar={isSidebar}
        />
      )}
    </div>
  );

  if (isSidebar) {
    return content;
  }

  return createPortal(
    <div className="fixed top-0 left-0 w-full h-screen bg-black/60 z-50 flex items-center justify-end p-4">
      <div className="rounded-2xl xl:w-7/12 lg:w-7/12 w-11/12 h-[calc(100vh-2rem)] relative">
        {content}
      </div>
    </div>,
    document.body
  );
};

export default AddOrdersModal;
